/* 공간 페이지 레이아웃 프리셋 — 요소를 새로 만들지 않고, 이미 있는 대표 사진·서브 사진·제목·본문의 위치와 크기만 다시 잡는다.
   그래서 손으로 고친 글이나 바꾼 사진은 그대로 유지된다. */
import { PAGE_H, PAGE_W, type El, type Page } from './types';

export const DEFAULT_ROOM_LAYOUT = 'hero';

export const isHeading = (e: El) => e.type === 'text' && !!e.bold && (e.fontSize || 0) >= 24;
export const textRole = (e: El) => (e.role === 'heading' || (!e.role && isHeading(e)) ? '제목' : '본문');

export function roomParts(page: Page) {
  const imgs = page.elements.filter((e) => e.type === 'image');
  const main = imgs.find((e) => e.role === 'main') || imgs[0];
  const subs = imgs.filter((e) => e !== main);
  const heading = page.elements.find((e) => e.role === 'heading') || page.elements.find(isHeading);
  const body =
    page.elements.find((e) => e.role === 'body') ||
    page.elements
      .filter((e) => e.type === 'text' && e !== heading)
      .sort((a, b) => (b.text || '').length - (a.text || '').length)[0];
  return { main, subs, heading, body };
}

export function isRoomPage(pages: Page[], i: number) {
  const p = pages[i];
  if (!p) return false;
  if (p.kind) return p.kind === 'room';
  const { main, heading } = roomParts(p);
  return i > 0 && !!main && !!heading;
}

export function layoutRoomPage(page: Page, preset: string): boolean {
  const { main, subs, heading, body } = roomParts(page);
  if (!main) return false;
  // 위·아래 여백을 양옆(48px)과 같게 — 제목 위에 작은 머리글("02 · KITCHEN")이 있으면 그만큼 아래에서 시작
  const M = 48, W = PAGE_W - M * 2, BOTTOM = 48, GAP = 6;
  const TOPY = M + (page.elements.some((e) => e.role === 'kicker') ? 26 : 0);
  const place = (e: El | undefined, x: number, y: number, w: number, h: number) => {
    if (!e) return;
    e.x = Math.round(x); e.y = Math.round(y); e.w = Math.round(w); e.h = Math.round(h);
  };
  const align = (e: El | undefined, a: 'left' | 'center') => { if (e) e.align = a; };
  const row = (list: El[], x: number, y: number, w: number, h: number) => {
    const n = list.length, cw = (w - GAP * (n - 1)) / n;
    list.forEach((e, i) => place(e, x + i * (cw + GAP), y, cw, h));
  };
  const hide = (list: El[]) => list.forEach((e) => place(e, PAGE_W - 2, PAGE_H - 2, 1, 1)); // 넘치는 사진은 거의 안 보이게

  if (preset === 'center') {
    place(heading, M, TOPY, W, 48); align(heading, 'center');
    const mainY = TOPY + 60, subRows = Math.ceil(subs.length / 4);
    const subH = subs.length ? (subRows > 1 ? 118 * 2 + GAP : 172) + GAP * 1.5 + 14 : 8;
    const mainH = Math.max(300, PAGE_H - BOTTOM - mainY - subH - 190); // 아래 글 칸 190px은 남긴다
    place(main, M, mainY, W, mainH);
    let y = mainY + mainH + GAP * 1.5;
    if (subs.length) {
      const th = subRows > 1 ? 118 : 172;
      for (let r = 0; r < subRows; r++) { row(subs.slice(r * 4, r * 4 + 4), M, y, W, th); y += th + GAP; }
      y += 14;
    } else y += 8;
    place(body, M, y, W, Math.max(80, PAGE_H - BOTTOM - y)); align(body, 'left');
  } else if (preset === 'hero') {
    // 대표 사진을 위에 꽉 차게, 바로 아래 서브 사진 한 줄도 끝까지, 제목·글은 그 아래
    place(main, 0, 0, PAGE_W, 470);
    let y = 470;
    if (subs.length) {
      const first = subs.slice(0, 3), more = subs.slice(3);
      row(first, 0, y + GAP, PAGE_W, more.length ? 150 : 190); y += GAP + (more.length ? 150 : 190);
      if (more.length) { row(more, 0, y + GAP, PAGE_W, 120); y += GAP + 120; }
    }
    place(heading, M, y + 30, W, 48); align(heading, 'left');
    place(body, M, y + 84, W, Math.max(80, PAGE_H - BOTTOM - (y + 84))); align(body, 'left');
  } else if (preset === 'grid') {
    // 사진 전부를 2열 격자로 크게 — 장수가 홀수면 대표 사진이 한 줄을 다 쓴다
    place(heading, M, TOPY, W, 48); align(heading, 'left');
    const all = [main, ...subs];
    const GX = 24, GW = PAGE_W - GX * 2, top = TOPY + 60, bodyH = 176;
    const availH = PAGE_H - BOTTOM - bodyH - 20 - top;
    const span = all.length % 2 === 1;
    const rows = span ? 1 + (all.length - 1) / 2 : all.length / 2;
    const ch = (availH - GAP * (rows - 1)) / rows, cw = (GW - GAP) / 2;
    all.forEach((e, i) => {
      if (span && i === 0) return place(e, GX, top, GW, ch);
      const k = span ? i - 1 : i, r = Math.floor(k / 2) + (span ? 1 : 0);
      place(e, GX + (k % 2) * (cw + GAP), top + r * (ch + GAP), cw, ch);
    });
    const y = top + rows * ch + (rows - 1) * GAP + 20;
    place(body, M, y, W, Math.max(80, PAGE_H - BOTTOM - y)); align(body, 'left');
  } else if (preset === 'split') {
    // 사진 반쪽형 — 위쪽 620px을 사진으로: 대표 사진 왼쪽 크게, 서브 사진은 오른쪽(4장이면 2×2)
    const PH = 620, s = subs.slice(0, 4);
    if (!s.length) place(main, 0, 0, PAGE_W, PH);
    else {
      const mw = s.length >= 4 ? 360 : s.length === 3 ? 420 : 440;
      place(main, 0, 0, mw, PH);
      const rx = mw + GAP, rw = PAGE_W - rx;
      if (s.length >= 4) {
        const cw = (rw - GAP) / 2, ch = (PH - GAP) / 2;
        s.forEach((e, i) => place(e, rx + (i % 2) * (cw + GAP), Math.floor(i / 2) * (ch + GAP), cw, ch));
      } else {
        const ch = (PH - GAP * (s.length - 1)) / s.length;
        s.forEach((e, i) => place(e, rx, i * (ch + GAP), rw, ch));
      }
    }
    hide(subs.slice(4));
    place(heading, M, PH + 30, W, 48); align(heading, 'left');
    place(body, M, PH + 84, W, PAGE_H - BOTTOM - (PH + 84)); align(body, 'left');
  } else if (preset === 'band') {
    // 띠형 — 제목·글 위, 대표 사진은 페이지 폭 끝까지, 서브 사진은 맨 아래 한 줄
    place(heading, M, TOPY, W, 48); align(heading, 'left');
    const bodyH = 190;
    place(body, M, TOPY + 58, W, bodyH); align(body, 'left');
    const top = TOPY + 58 + bodyH + 20, s = subs.slice(0, 4), subH = s.length ? 176 : 0;
    place(main, 0, top, PAGE_W, PAGE_H - top - (s.length ? subH + GAP : 0));
    if (s.length) row(s, 0, PAGE_H - subH, PAGE_W, subH);
    hide(subs.slice(4));
  } else if (preset === 'side') {
    // 사이드형 — 대표 사진을 왼쪽에 세로로 페이지 높이 끝까지, 오른쪽 위에 서브 사진(최대 2장), 그 아래 제목·글
    const MW = 400, rx = MW + GAP, rw = PAGE_W - rx, s = subs.slice(0, 2);
    place(main, 0, 0, MW, PAGE_H);
    const sh = s.length === 2 ? 196 : 300;
    s.forEach((e, i) => place(e, rx, i * (sh + GAP), rw, sh));
    hide(subs.slice(2));
    const ty = s.length ? s.length * (sh + GAP) + 28 : TOPY, tx = rx + 24, tw = rw - 48;
    place(heading, tx, ty, tw, 44); align(heading, 'left');
    place(body, tx, ty + 54, tw, PAGE_H - BOTTOM - (ty + 54)); align(body, 'left');
  } else return false;
  // 템플릿의 작은 머리글("01 · LIVING" 같은)은 제목 바로 위에 따라 붙인다
  const kicker = page.elements.find((e) => e.role === 'kicker');
  if (kicker && heading) {
    place(kicker, heading.x, Math.max(8, heading.y - 24), heading.w, 22);
    kicker.align = heading.align;
  }
  page.layout = preset;
  return true;
}
